import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { FacetData } from '~/contracts/common';
import type { RecentSearch } from '~/contracts/home';
import { DeepPartial } from '~/types';

export type RecentSearchesContextType = {
  baseline: Partial<FacetData>;
  fetchRecentSearchesTrigger: number;
  recentSearches: LoadableState<RecentSearch[]>;
  triggerFetchRecentSearches: () => void;
};

export const defaultRecentSearchesContext: RecentSearchesContextType = {
  baseline: {},
  fetchRecentSearchesTrigger: 0,
  recentSearches: defaultLoadableState<RecentSearch[]>([]),
  triggerFetchRecentSearches() {}
};

const RecentSearchesContext = createContext<RecentSearchesContextType>(
  defaultRecentSearchesContext
);

export type OwnProps = PropsWithChildren<{
  baseline: Partial<FacetData>;
}>;

export function RecentSearchesContextProvider(props: OwnProps) {
  const recentSearches = useLoadableState<RecentSearch[]>([]);
  const [fetchRecentSearchesTrigger, setFetchRecentSearchesTrigger] =
    useState(0);

  const triggerFetchRecentSearches = useCallback(
    () => setFetchRecentSearchesTrigger(current => current + 1),
    []
  );

  const value = useMemo<RecentSearchesContextType>(
    () => ({
      baseline: props.baseline,
      fetchRecentSearchesTrigger,
      recentSearches,
      triggerFetchRecentSearches
    }),
    [
      fetchRecentSearchesTrigger,
      props.baseline,
      recentSearches,
      triggerFetchRecentSearches
    ]
  );

  return (
    <RecentSearchesContext.Provider value={value}>
      {props.children}
    </RecentSearchesContext.Provider>
  );
}

RecentSearchesContextProvider.displayName = 'RecentSearchesContextProvider';

export const generateRecentSearches = (
  customState: DeepPartial<RecentSearchesContextType>
): RecentSearchesContextType =>
  deepmerge<RecentSearchesContextType, DeepPartial<RecentSearchesContextType>>(
    defaultRecentSearchesContext,
    customState
  );

export function MockedRecentSearchesContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{
  mockValue: DeepPartial<RecentSearchesContextType>;
}>) {
  return (
    <RecentSearchesContext.Provider value={generateRecentSearches(mockValue)}>
      {children}
    </RecentSearchesContext.Provider>
  );
}

export function useGetRecentSearches(): RecentSearch[] {
  return useContext(RecentSearchesContext).recentSearches.data;
}

export function useBaseline(): Partial<FacetData> {
  return useContext(RecentSearchesContext).baseline;
}

export default RecentSearchesContext;
